import React, { useState, useEffect } from 'react'
import type { ClienteNota } from '../../lib/supabase'

interface NotasListProps {
  clienteId: string
  onEditNota?: (nota: ClienteNota) => void
  onDeleteNota?: (notaId: string) => void
  refreshTrigger?: number
}

const NotasList: React.FC<NotasListProps> = ({ 
  clienteId, 
  onEditNota, 
  onDeleteNota,
  refreshTrigger = 0 
}) => {
  const [notas, setNotas] = useState<ClienteNota[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedNota, setExpandedNota] = useState<string | null>(null)

  useEffect(() => {
    fetchNotas()
  }, [clienteId, refreshTrigger])

  const fetchNotas = async () => {
    try {
      setLoading(true)
      console.log(`🔍 Carregando notas do cliente: ${clienteId}`)

      const response = await fetch(`/api/gestao/cliente-notas?cliente_id=${clienteId}`)
      
      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`)
      }
      
      const result = await response.json()
      console.log(`✅ ${result.data?.length || 0} notas carregadas`)
      
      setNotas(result.data || [])
      
    } catch (error) {
      console.error('❌ Erro ao carregar notas:', error)
      setNotas([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (notaId: string) => {
    if (!confirm('Tem certeza que deseja deletar esta nota?')) {
      return
    }

    try {
      const response = await fetch(`/api/gestao/cliente-notas?id=${notaId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error(`Erro ao deletar: ${response.status}`)
      }

      // Remover da lista local
      setNotas(notas.filter(nota => nota.id !== notaId))
      
      if (onDeleteNota) {
        onDeleteNota(notaId)
      }

      console.log(`✅ Nota deletada: ${notaId}`)
      
    } catch (error) {
      console.error('❌ Erro ao deletar nota:', error)
      alert('Erro ao deletar nota. Tente novamente.')
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTipoNotaColor = (tipo: string) => {
    switch (tipo) {
      case 'importante':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'lembrete':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'comercial':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'financeiro':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTipoNotaLabel = (tipo: string) => {
    switch (tipo) {
      case 'importante':
        return 'Importante'
      case 'lembrete':
        return 'Lembrete'
      case 'comercial':
        return 'Comercial'
      case 'financeiro':
        return 'Financeiro'
      default:
        return 'Geral'
    }
  }

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return '🖼️'
    } else if (type === 'application/pdf') {
      return '📄'
    } else if (type.includes('word') || type.includes('document')) {
      return '📝'
    } else {
      return '📎'
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileTypeLabel = (type: string) => {
    if (type.startsWith('image/')) {
      return 'Imagem'
    } else if (type === 'application/pdf') {
      return 'PDF'
    } else if (type.includes('word') || type.includes('document')) {
      return 'Documento'
    } else if (type === 'text/plain') {
      return 'Texto'
    } else if (type === 'text/csv') {
      return 'CSV'
    } else {
      return 'Arquivo'
    }
  }

  const handleDeleteAnexo = async (anexoId: string) => {
    if (!confirm('Tem certeza que deseja deletar este anexo?')) {
      return
    }

    try {
      const response = await fetch(`/api/gestao/cliente-nota-anexos?id=${anexoId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error(`Erro ao deletar anexo: ${response.status}`)
      }

      // Recarregar notas para atualizar a lista
      await fetchNotas()
      console.log(`✅ Anexo deletado: ${anexoId}`)
      
    } catch (error) {
      console.error('❌ Erro ao deletar anexo:', error)
      alert('Erro ao deletar anexo. Tente novamente.')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-600">Carregando notas...</p>
        </div>
      </div>
    )
  }

  if (notas.length === 0) {
    return (
      <div className="text-center py-8">
        <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500 mb-2">Nenhuma nota encontrada</p>
        <p className="text-gray-400 text-sm">Clique em "Nova Nota" para adicionar a primeira nota deste cliente</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {notas.map((nota) => (
        <div key={nota.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
          <div className="p-4">
            {/* Header da Nota */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">{nota.titulo}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTipoNotaColor(nota.tipo_nota)}`}>
                    {getTipoNotaLabel(nota.tipo_nota)}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>📅 {formatDate(nota.created_at)}</span>
                  <span>👤 {nota.usuario_nome || 'Sistema'}</span>
                  {nota.anexos && nota.anexos.length > 0 && (
                    <span>📎 {nota.anexos.length} anexo{nota.anexos.length > 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
              
              {/* Botões de Ação */}
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setExpandedNota(expandedNota === nota.id ? null : nota.id)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title={expandedNota === nota.id ? 'Recolher' : 'Expandir'}
                >
                  <svg className={`w-4 h-4 transform transition-transform ${expandedNota === nota.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {onEditNota && (
                  <button
                    onClick={() => onEditNota(nota)}
                    className="p-1 text-blue-400 hover:text-blue-600 transition-colors"
                    title="Editar nota"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
                
                <button
                  onClick={() => handleDelete(nota.id)}
                  className="p-1 text-red-400 hover:text-red-600 transition-colors"
                  title="Deletar nota"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Preview do Conteúdo */}
            <div className="text-gray-700">
              {expandedNota === nota.id ? (
                <div className="whitespace-pre-wrap">{nota.conteudo}</div>
              ) : (
                <p className="line-clamp-2">
                  {nota.conteudo.length > 150 
                    ? `${nota.conteudo.substring(0, 150)}...` 
                    : nota.conteudo
                  }
                </p>
              )}
            </div>

            {/* Anexos */}
            {nota.anexos && nota.anexos.length > 0 && expandedNota === nota.id && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Anexos:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {nota.anexos.map((anexo) => (
                    <div key={anexo.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                          {getFileIcon(anexo.tipo_arquivo)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{anexo.nome_arquivo}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{formatFileSize(anexo.tamanho_arquivo)}</span>
                            <span>•</span>
                            <span>{getFileTypeLabel(anexo.tipo_arquivo)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <a
                          href={anexo.url_arquivo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                          title="Baixar arquivo"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </a>
                        <button
                          onClick={() => handleDeleteAnexo(anexo.id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                          title="Deletar anexo"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Preview de Imagens */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {nota.anexos
                    .filter(anexo => anexo.tipo_arquivo.startsWith('image/'))
                    .map((anexo) => (
                      <div key={`preview-${anexo.id}`} className="relative group">
                        <img
                          src={anexo.url_arquivo}
                          alt={anexo.nome_arquivo}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                          <a
                            href={anexo.url_arquivo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-2 shadow-lg transition-opacity"
                          >
                            <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default NotasList
